import type { Asserts } from 'yup';
import { previousInsuranceSchema } from '@/features/Sparten/pkv/schema/previousInsuranceSchema/previousInsuranceSchema';
import { defineStore } from 'pinia';
import type { PreviousInsuranceProductCategory } from '@/features/Sparten/pkv/types';
import { useApi } from '@/services/api/useApi';
import { VglError } from '@/services/errorHandler/vglError';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import hashCode from '@/utils/hashCode/hashCode';

export type PkvPreviousInsuranceStore = Asserts<typeof previousInsuranceSchema>;

const baseState = previousInsuranceSchema.getDefault();
export interface PkvPreviousInsuranceStoreState
  extends PkvPreviousInsuranceStore {
  data: PreviousInsuranceProductCategory[];
  isLoading: boolean;
  loadedInsuranceId: string | null;
}

export const usePkvPreviousInsuranceStore = defineStore(
  'PkvPreviousInsuranceStore',
  {
    state: (): PkvPreviousInsuranceStoreState => ({
      data: [],
      isLoading: false,
      loadedInsuranceId: null,
      ...baseState,
    }),

    actions: {
      async loadProductCategories(
        insuranceId: string,
        queryParams?: Record<string, unknown>,
      ) {
        try {
          if (
            this.loadedInsuranceId ===
            hashCode({
              insuranceId,
              queryParams,
            })
          ) {
            return;
          }

          const api = useApi().previousInsurance;
          this.isLoading = true;
          const [, request] = api.loadProducts(insuranceId, queryParams);

          const result = await request;
          if (result === null) {
            throw new VglError(
              VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE,
              {
                additionalErrorMessage:
                  'previousInsuranceInput -> loadProducts',
              },
            );
          }

          this.data = result;
          this.loadedInsuranceId = insuranceId;
        } catch (error) {
          throw new VglError(
            VGL_ERROR_CODE.PKV_PREVIOUS_INSURANCE_LOADING_FAILED,
            {
              additionalErrorMessage: `Error: ${getErrorMessage(
                error,
              )} | InsuranceID: ${insuranceId} | Params: ${JSON.stringify(
                queryParams,
              )}`,
              cause: error,
            },
          );
        } finally {
          this.isLoading = false;
        }
      },
    },
  },
);
